/**
 * Face Landmarks Adapter
 *
 * Thin adapter to connect FaceLandmarksComponent to the app's store and navigation.
 */

import { FaceLandmarksComponent } from './index';
import { store } from '../../app/store';

// Singleton detector instance for preloading
let detectorInstance: FaceLandmarksComponent | null = null;
let preloadPromise: Promise<void> | null = null;

/**
 * Call this early (at app startup or selfie-intro screen) to preload ML models (~5MB)
 * in the background. This makes the camera screen load instantly.
 */
export function preloadFaceLandmarks(): void {
  if (preloadPromise) return;

  // Create a temporary container for preloading
  const tempContainer = document.createElement('div');
  tempContainer.style.display = 'none';
  document.body.appendChild(tempContainer);

  detectorInstance = new FaceLandmarksComponent(tempContainer);
  preloadPromise = detectorInstance.preload().finally(() => {
    // Clean up temp container after preload
    if (detectorInstance) {
      detectorInstance.dispose();
      detectorInstance = null;
    }
    document.body.removeChild(tempContainer);
  });
}

export interface FaceLandmarksController {
  start: () => void;
  stop: () => void;
  dispose: () => void;
}

/**
 * Initialize the face landmarks camera on a container element.
 * Call preloadFaceLandmarks() earlier to avoid waiting here.
 */
export async function initFaceLandmarksCamera(
  container: HTMLElement
): Promise<FaceLandmarksController> {
  // Wait for preload to complete (instant if already called earlier)
  if (preloadPromise) {
    await preloadPromise;
  }

  const component = new FaceLandmarksComponent(container);

  // Preload models if not already done
  await component.preload();

  // Initialize camera
  await component.init();

  // Request screenshot capture at end of Phase 2 (lines)
  component.getScreenshotWhenReady().then(({ withMesh, withoutMesh }) => {
    store.set('selfie', withMesh);
    store.set('selfieWithoutMesh', withoutMesh);
  });

  // When animation completes, dispatch event to show continue button
  component.on('onComplete', () => {
    document.dispatchEvent(new CustomEvent('face-landmarks:complete'));
  });

  return {
    start: () => component.start(),
    stop: () => component.stop(),
    dispose: () => component.dispose()
  };
}
